import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, Grid, Card, CardMedia, CardContent,
  CardActions, Chip, CircularProgress, Fade, Zoom, IconButton
} from '@mui/material';
import { ShoppingCart, LocalOffer, AccessTime, Share } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Theme styles
const getThemeStyles = (theme, design) => {
  const themes = {
    modern: {
      fontFamily: "'Inter', sans-serif",
      borderRadius: 12,
      shadow: '0 4px 20px rgba(0,0,0,0.1)'
    },
    classic: {
      fontFamily: "'Georgia', serif",
      borderRadius: 4,
      shadow: '0 2px 10px rgba(0,0,0,0.15)'
    },
    minimal: {
      fontFamily: "'Roboto', sans-serif",
      borderRadius: 0,
      shadow: 'none'
    },
    festive: {
      fontFamily: "'Poppins', sans-serif",
      borderRadius: 16,
      shadow: '0 8px 30px rgba(255,87,34,0.2)'
    },
    dark: {
      fontFamily: "'Space Grotesk', sans-serif",
      borderRadius: 8,
      shadow: '0 4px 20px rgba(0,0,0,0.4)'
    }
  };
  return themes[theme] || themes.modern;
};

const CampaignLandingPage = () => {
  const { shortCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCampaignData();
    trackView();
  }, [shortCode]);

  const fetchCampaignData = async () => {
    try {
      const res = await axios.get(`/api/campaign-links/view/${shortCode}`);
      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError('Campaign not found');
      }
    } catch (err) {
      setError('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const trackView = async () => {
    try {
      await axios.post(`/api/campaign-links/track/${shortCode}`, { action: 'view' });
    } catch (err) {
      console.error('Track error:', err);
    }
  };

  const handleCTAClick = async () => {
    try {
      await axios.post(`/api/campaign-links/track/${shortCode}`, { action: 'click' });
      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl;
      }
    } catch (err) {
      console.error('Track error:', err);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: data?.title,
        text: data?.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied!');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column">
        <Typography variant="h5" color="error" gutterBottom>😕 {error}</Typography>
        <Typography color="textSecondary">This campaign may have expired or been removed.</Typography>
      </Box>
    );
  }

  // Demo data for preview (replace with actual data)
  const demoData = {
    title: 'Weekend Special Offers! 🎉',
    description: 'Enjoy our exclusive weekend deals on your favorite dishes',
    offerDetails: 'Get 20% OFF on all orders above ₹500',
    design: {
      backgroundColor: '#ffffff',
      textColor: '#333333',
      accentColor: '#FF5722',
      ctaButtonColor: '#4CAF50',
      ctaButtonText: 'Order Now',
      layout: 'grid',
      theme: 'modern',
      showPrices: true,
      showDescriptions: true,
      headerImage: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800',
      logoUrl: ''
    },
    products: [
      { name: 'Butter Chicken', price: 350, image: 'https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?w=400', description: 'Creamy tomato-based curry' },
      { name: 'Paneer Tikka', price: 280, image: 'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=400', description: 'Grilled cottage cheese' },
      { name: 'Biryani', price: 320, image: 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=400', description: 'Aromatic rice dish' },
      { name: 'Naan Bread', price: 60, image: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=400', description: 'Fresh baked bread' }
    ],
    expiryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
  };

  const displayData = data || demoData;
  const design = displayData.design || demoData.design;
  const themeStyles = getThemeStyles(design.theme, design);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: design.backgroundColor,
        color: design.textColor,
        fontFamily: themeStyles.fontFamily,
        pb: 4
      }}
    >
      {/* Header Image */}
      {design.headerImage && (
        <Fade in timeout={800}>
          <Box
            sx={{
              width: '100%',
              height: { xs: 200, md: 300 },
              backgroundImage: `linear-gradient(to bottom, transparent 60%, ${design.backgroundColor}), url(${design.headerImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
        </Fade>
      )}

      <Container maxWidth="md" sx={{ mt: design.headerImage ? -8 : 4, position: 'relative' }}>
        {/* Logo */}
        {design.logoUrl && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img src={design.logoUrl} alt="Logo" style={{ maxHeight: 80, maxWidth: 200 }} />
          </Box>
        )}

        {/* Title & Description */}
        <Zoom in timeout={500}>
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{ color: design.accentColor, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}
            >
              {displayData.title}
            </Typography>
            
            {displayData.description && (
              <Typography variant="h6" sx={{ opacity: 0.8, mb: 2 }}>
                {displayData.description}
              </Typography>
            )}

            {/* Offer Badge */}
            {displayData.offerDetails && (
              <Chip
                icon={<LocalOffer />}
                label={displayData.offerDetails}
                sx={{
                  bgcolor: `${design.accentColor}20`,
                  color: design.accentColor,
                  fontSize: '1rem',
                  py: 2.5,
                  px: 2,
                  borderRadius: themeStyles.borderRadius
                }}
              />
            )}

            {/* Expiry Timer */}
            {displayData.expiryDate && new Date(displayData.expiryDate) > new Date() && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                <AccessTime sx={{ color: design.accentColor }} />
                <Typography variant="body2" sx={{ color: design.accentColor }}>
                  Offer ends: {new Date(displayData.expiryDate).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Box>
        </Zoom>

        {/* Products Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {displayData.products?.map((product, index) => (
            <Grid
              item
              xs={design.layout === 'list' ? 12 : 6}
              sm={design.layout === 'list' ? 12 : 6}
              md={design.layout === 'list' ? 12 : 4}
              key={index}
            >
              <Fade in timeout={600 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    display: design.layout === 'list' ? 'flex' : 'block',
                    borderRadius: `${themeStyles.borderRadius}px`,
                    boxShadow: themeStyles.shadow,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  {product.image && (
                    <CardMedia
                      component="img"
                      sx={{
                        height: design.layout === 'list' ? 120 : 180,
                        width: design.layout === 'list' ? 120 : '100%',
                        objectFit: 'cover'
                      }}
                      image={product.image}
                      alt={product.name}
                    />
                  )}
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {product.name}
                    </Typography>
                    {design.showDescriptions && product.description && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {product.description}
                      </Typography>
                    )}
                    {design.showPrices && (
                      <Typography
                        variant="h6"
                        sx={{ color: design.accentColor, fontWeight: 700 }}
                      >
                        ₹{product.price}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* CTA Button */}
        <Box sx={{ textAlign: 'center', position: 'sticky', bottom: 20, zIndex: 10 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<ShoppingCart />}
            onClick={handleCTAClick}
            sx={{
              bgcolor: design.ctaButtonColor,
              color: '#fff',
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 700,
              borderRadius: `${themeStyles.borderRadius}px`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: design.ctaButtonColor,
                filter: 'brightness(0.9)',
                transform: 'scale(1.02)'
              }
            }}
          >
            {design.ctaButtonText || 'Order Now'}
          </Button>

          {/* Share Button */}
          <IconButton
            onClick={handleShare}
            sx={{ ml: 2, bgcolor: 'rgba(0,0,0,0.1)', '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' } }}
          >
            <Share />
          </IconButton>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 6, opacity: 0.5 }}>
          <Typography variant="caption">
            Powered by Kooppai
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CampaignLandingPage;
